import {Card} from "../atoms/Card";
import React, {useState} from "react";
import {
  autoconfigureInboxAPI,
  autoconfigureOutboxAPI,
  autoconfigureOutboxRecipientsAPI,
  createNewNotionProperties,
  createNotionPage,
  removeInboxForDatabaseAPI,
  sendPageAPI,
  setupInboxForDatabaseAPI,
  updateDatabaseConfigAPI
} from "../../../services/api";
import {useOutboxPages} from "../../../data/pages";
import {URLS} from "../../../constants";
import {DatabaseInfo} from "@app/core-app/app/data/notion/DatabaseInfo";
import {UserProfileData} from "@app/core-app/app/usecase/fetch-user-profile";
import {PROFILE_DATA_KEY} from "../../../data/profile";
import {useSWRConfig} from "swr";
import {
  Heading,
  Tabs,
  TabList,
  Tab,
  TabPanels,
  TabPanel, Stack
} from "@chakra-ui/react";
import {
  DataExtractionProperty,
  DEFAULT_DATA_EXTRACTION_CONFIG,
  DEFAULT_OUTBOX_CONFIG
} from "@app/core-app/app/data/profile";
import {SendEmailsTab} from "../molecules/SendEmailsTab";
import {ReceiveEmailsTab} from "../molecules/ReceiveEmailsTab";
import {getInboxProperties} from "@app/core-app/app/domain/notion/db-properties";
import {useStatusToast} from "../../../hooks/useStatusToast";
import {formatKeyToEmail} from "@app/core-app/app/domain/email";
import {
  getLegacyOutboxProperties,
  getV2OutboxProperties
} from "@app/core-app/app/domain/notion/configurations/outbox-props";
import {useOutboxLog} from "../../../data/outbox-log";
import {IconLink} from "../atoms/IconLink";
import {ProfileService} from "@app/core-app/app/service/profile-service";


export type DatabaseCardProps = {
  database: DatabaseInfo
  profile: UserProfileData
}


export const DatabaseCard = ({database, profile}: DatabaseCardProps) => {
  const outboxProperties = database.outboxConfig.type == "v2" ?
    getV2OutboxProperties(database.outboxConfig.report, database) :
    getLegacyOutboxProperties(database.outboxConfig.report)
  const inboxProperties = getInboxProperties(database)

  const {mutate: mutateGlobal} = useSWRConfig()
  const mutateProfile = () => mutateGlobal(PROFILE_DATA_KEY)

  const dbConfig = profile.profile.dbConfigurations?.find(({databaseId}) => databaseId === database.id)
  const inboxKey = dbConfig?.key

  // logger.info({gmailSubscription, database})
  const [tabIndex, setTabIndex] = useState(0)
  const shouldLoadOutbox = tabIndex === 1
  const {data, isLoading, isError, mutate, isValidating} = useOutboxPages((shouldLoadOutbox) ? database.id : null)
  const {data: outboxLog, isLoading: isOutboxLoading} = useOutboxLog((shouldLoadOutbox) ? database.id : null)

  const {addToast, updateToast, addToastResult, updateToastResult, toastCatch, toast} = useStatusToast()

  return <Card>

    <Stack direction={"row"} alignItems={"center"} spacing={4}>
      <Heading marginX={[0]} marginY={"1.5"} size={"lg"} fontWeight={"medium"}>{database.name}</Heading>
      {database.url &&
        <IconLink url={database.url}/>
      }
    </Stack>

    <Tabs isLazy onChange={(index) => setTabIndex(index)}>
      <TabList>
        <Tab>Receive emails</Tab>
        <Tab>Send emails</Tab>
      </TabList>

      <TabPanels>
        <TabPanel paddingX={[0]}>
          <ReceiveEmailsTab
            database={database}
            inboxEmail={inboxKey ? formatKeyToEmail(inboxKey) : null}
            inboxProperties={inboxProperties}
            inboxConfiguration={dbConfig?.inbox ?? {saveAs: "plain_text"}}
            onDropAIProperty={(propertyName) => {
              const dbConfig = profile.profile.dbConfigurations?.find(db => db.databaseId === database.id)
              const inboxConfig = dbConfig?.inbox
              if (inboxConfig) {
                const currentExtractionConfig = dbConfig.inbox?.dataExtractionConfig || DEFAULT_DATA_EXTRACTION_CONFIG
                const currentProperties = currentExtractionConfig?.properties ?? []
                const updatedProperties = currentProperties.filter(p => p.propertyName !== propertyName)
                addToast({title: "Removing AI property..."})
                return updateDatabaseConfigAPI(database.id, {
                  outbox: undefined,
                  inbox: {
                    ...inboxConfig, dataExtractionConfig: {
                      ...(currentExtractionConfig),
                      properties: updatedProperties,

                    }
                  }
                }).then((r) => updateToastResult(r, {title: "AI Property removed 🎉"}))
                  .then((r) => mutateProfile())
                  .catch((e) => toastCatch(e))
              }

              return Promise.resolve()
            }}
            onCreateNewAIProperty={(data: any) => {
              let propertyDataPromise = Promise.resolve(data)
              if (data.isNew) {
                propertyDataPromise = createNewNotionProperties(database, {
                  [data.propertyName]: {
                    name: data.propertyName,
                    type: "rich_text",
                    rich_text: {}
                  },
                }).then((r) => {
                  const property = r.data?.propertiesObjects
                    ?.find((p) => p.name === data.propertyName)

                  if (!property) {
                    throw new Error("Failed to configure new property.")
                  }
                  return {
                    propertyId: property.id,
                    propertyName: data.propertyName,
                    propertyDescription: data.propertyDescription,
                  }

                })
              }

              return propertyDataPromise.then(({propertyId, propertyName, propertyDescription}) => {
                const dbConfig = profile.profile.dbConfigurations?.find(db => db.databaseId === database.id)
                const inboxConfig = dbConfig?.inbox
                if (inboxConfig) {

                  const currentExtractionConfig = dbConfig.inbox?.dataExtractionConfig || DEFAULT_DATA_EXTRACTION_CONFIG
                  const currentProperties = currentExtractionConfig?.properties ?? []
                  const updatedProperties: DataExtractionProperty[] = [...currentProperties, {
                    propertyId: propertyId,
                    propertyName: propertyName,
                    propertyDescription: propertyDescription.length > 0 ? propertyDescription : propertyName
                  }]
                  addToast({title: "Adding new property..."})
                  return updateDatabaseConfigAPI(database.id, {
                    outbox: undefined,
                    inbox: {
                      ...inboxConfig, dataExtractionConfig: {
                        ...(currentExtractionConfig),
                        properties: updatedProperties,

                      }
                    }
                  })
                    .then((r) => updateToastResult(r, {title: "Property created. "}))
                    .then((r) => mutateProfile())
                    .catch((e) => toastCatch(e))
                } else {
                  addToast({title: "Please enable inbox before.", colorScheme: "orange"})
                }

                return Promise.resolve()

              })
            }}

            onCreateNewDefaultProperty={(property) => {
              addToast({title: "Adding new property"})
              const result = ProfileService.addDefaultPropertyToInbox({
                profile: profile.profile,
                databaseId: database.id,
                property
              })
              return updateDatabaseConfigAPI(database.id, {...result})
                .then((r) => mutateProfile())
                .then((r) => updateToastResult(r, {title: "Property created."}))
                .catch((e) => toastCatch(e))

            }}

            onDropDefaultProperty={(propertyName) => {
              addToast({title: "Removing property"})
              const result = ProfileService.removeDefaultPropertyFromInbox({
                profile: profile.profile,
                databaseId: database.id,
                propertyName
              })
              return updateDatabaseConfigAPI(database.id, {...result})
                .then((r) => mutateProfile())
                .then((r) => updateToastResult(r, {title: "Property removed."}))
                .catch((e) => toastCatch(e))

            }}

            onCreateInbox={() => {
              addToast({title: "Creating inbox"})
              setupInboxForDatabaseAPI(database)
                .then((r) => updateToastResult(r, {title: "Inbox created 🎉"}))
                .then((r) => mutateProfile())
                .catch((e) => toastCatch(e))
            }}
            onDeleteInbox={() => {
              addToast({title: "Deleting inbox"})
              removeInboxForDatabaseAPI(database)
                .then((r) => updateToastResult(r, {title: "Inbox deleted 🎉"}))
                .then(() => mutateProfile())
                .catch((e) => toastCatch(e))
            }}

            onAutoconfigure={() => {
              addToast({title: "Creating properties"})
              autoconfigureInboxAPI(database)
                .then((r) => updateToastResult(r, {title: "Properties created 🎉"}))
                .then(() => mutateProfile())
                .catch(toastCatch)
            }}

            onConfigurationChange={(config) => {
              updateDatabaseConfigAPI(database.id, {outbox: undefined, inbox: config})
                .then((d) => addToastResult(d, {title: "Updated 🎉", status: "success"}))
                .then(() => mutateProfile())
                .catch((e) => toastCatch(e))

            }}

          />

        </TabPanel>


        <TabPanel paddingX={[0]}>
          <SendEmailsTab
            pages={data || []}
            database={database}
            profile={profile}
            outboxLog={outboxLog}
            isOutboxLoading={isOutboxLoading}
            isLoadingPages={isValidating}
            outboxProperties={outboxProperties}
            outboxConfig={dbConfig?.outbox ?? DEFAULT_OUTBOX_CONFIG}
            onRevalidate={() => {
              mutate()
            }}
            onPageSend={(page) => {
              addToast({title: "Sending email"})
              return sendPageAPI(database, page)
                .then((r) => toast({title: "Emails are scheduled 🎉", status: "success"}))
                .then(() => mutate())
                // .then(() => mutateProfile())
                .catch((e) => toastCatch(e))

            }}
            onPagePreview={(page) => {
              window.open(encodeURI(`${URLS.CREATE_PAGE}?pageUrl=${page.url}`))
            }}
            onAutoconfigure={() => {
              addToast({title: "Creating properties"})
              autoconfigureOutboxAPI(database)
                .then((r) => updateToastResult(r, {title: "Properties created 🎉"}))
                .then(() => mutateProfile())
                .catch((e) => toastCatch(e))

            }}
            onRecipientsAutoconfigure={() => {
              if (dbConfig?.outbox?.recipientsDatabaseId) {
                addToast({title: "Configuring recipients database"})
                return autoconfigureOutboxRecipientsAPI(dbConfig.outbox.recipientsDatabaseId)
                  .then((r) => updateToastResult(r, {title: "Database created 🎉"}))
                  .then(() => mutateProfile())
                  .catch((e) => toastCatch(e))
              }
              return Promise.resolve()
            }}
            onConfigurationChange={(config) => {
              return updateDatabaseConfigAPI(database.id, {outbox: config, inbox: undefined})
                .then(() => addToast({title: "Updated 🎉", status: "success"}))
                .then(() => mutateProfile())
                .catch((e) => toastCatch(e))
            }}

            onCreateNewEmail={() => {
              return createNotionPage({databaseId: database.id})
                .then((result) => {
                  if (result.data) {
                    if (result.data.url) {
                      window.open(result.data.url, "_blank");
                    }
                  }

                })
                .then(() => mutate())
                .catch((e) => toastCatch(e))
            }}
          />


        </TabPanel>
      </TabPanels>
    </Tabs>
  </Card>

}
